import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';

import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';

import { ONBOARDING_TUTORIAL_LABEL } from '../../constants/onboarding-state/onboarding-editor-constants';

import type {
	OnboardingLabelsQuery as OnboardingLabelsQueryType,
	OnboardingLabelsQueryVariables as OnboardingLabelsQueryVariablesType,
} from './__types__/LabelsQuery';
import { LabelsQuery } from './LabelsQuery.graphql';

type useCheckIfOnboardingEditorTemplateProps = {
	contentId: string;
	skip?: boolean;
};

type useCheckIfOnboardingEditorTemplatePropsReturn = {
	isTemplate: boolean;
	isOnboardingTemplate: boolean;
	labelsData: OnboardingLabelsQueryType | undefined;
	labelsLoading: boolean;
	labelsError: ApolloErrorInterop | undefined;
};

export const useCheckIfOnboardingEditorTemplate = ({
	contentId,
	skip,
}: useCheckIfOnboardingEditorTemplateProps): useCheckIfOnboardingEditorTemplatePropsReturn => {
	const {
		data: labelsData,
		loading: labelsLoading,
		error: labelsError,
	} = useQuery<OnboardingLabelsQueryType, OnboardingLabelsQueryVariablesType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
		LabelsQuery,
		{
			variables: {
				contentId,
				status: ['current', 'draft'],
			},
			skip,
		},
	);

	const labels = labelsData?.content?.nodes?.[0]?.labels?.nodes || [];
	const sourceTemplateEntityId =
		labelsData?.content?.nodes?.[0]?.metadata?.sourceTemplateEntityId || undefined;

	// If the current editor is NOT the onboarding editor tutorial, show the draft banner.
	const isOnboardingTemplate = Boolean(
		labels.find((label) => label?.label === ONBOARDING_TUTORIAL_LABEL),
	);

	if (labelsError && isUnauthorizedError(labelsError)) {
		markErrorAsHandled(labelsError);
	}

	return {
		isTemplate: !!sourceTemplateEntityId,
		isOnboardingTemplate,
		labelsData,
		labelsLoading,
		labelsError,
	};
};
